import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { useNavigate } from "react-router-dom";
import PayeeFormModal from "./PayeeformModal";
import {
  createPayee,
  deletePayee,
  getPayees,
  updatePayee,
} from "../../api/payee";
import DeleteConfirmationModal from "../../components/deleteModal";

const PayeesPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [payees, setPayees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deletePayeeId, setDeletePayeeId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingPayee, setEditingPayee] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [
      address.address_1,
      address.city,
      address.country,
      address.postcode,
    ];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const fetchPayees = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getPayees(LIMIT, offset);
      setPayees(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load payees.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayeeSubmit = async (info) => {
    try {
      setSaving(true);

      const payload = {
        email: info.email,
        name: info.name,
        type: info.type,
        swift_bic: info.swift_bic,
        iban: info.iban,
        address: {
          address_1: info.address_1,
          country: info.country,
          city: info.city,
          postcode: info.postcode,
        },
      };

      if (editingPayee) {
        await updatePayee(editingPayee.id, payload);
      } else {
        await createPayee(payload);
      }

      setEditingPayee(null);
      setShowAddModal(false);

      fetchPayees();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayee = async () => {
    try {
      setDeleting(true);
      await deletePayee(deletePayeeId);

      setDeletePayeeId(null);

      fetchPayees();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchPayees();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Payees</h1>
          <p className="text-sm text-slate-400">Manage Payee info.</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add Payee
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Payee Info</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">IBAN</th>
                <th className="px-6 py-4">Swift (BIC)</th>
                <th className="px-6 py-4">Address</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {payees.map((py) => (
                <tr
                  key={py.id}
                  className="border-b border-white/5 last:border-none text-center hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {py.name}
                    </div>
                    <div className="text-sm font-medium text-slate-300">
                      {py.email}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {py.type}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {py.iban}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {py.swift_bic}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {formatAddress(py.address)}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingPayee(py)}
                        className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => setDeletePayeeId(py.id)}
                        className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete payee"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {payees.length === 0 && (
          <div className="p-8 text-center text-slate-400">No payees found.</div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <PayeeFormModal
        isOpen={showAddModal || !!editingPayee}
        onClose={() => {
          setShowAddModal(false);
          setEditingPayee(null);
        }}
        onSubmit={handlePayeeSubmit}
        loading={saving}
        mode={editingPayee ? "edit" : "create"}
        initialData={editingPayee}
      />

      <DeleteConfirmationModal
        isOpen={!!deletePayeeId}
        description="Are you sure you want to delete this payee? This action cannot be undone."
        onClose={() => setDeletePayeeId(null)}
        onConfirm={handleDeletePayee}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default PayeesPage;
